/**
 * Type utilities for type-first development.
 *
 * These utilities provide compile-time safety for working with
 * discriminated unions and exhaustive pattern matching.
 *
 * @example
 * ```typescript
 * // Exhaustive switch with assertNever
 * type Status = 'pending' | 'running' | 'completed' | 'failed'
 *
 * function getStatusColor(status: Status): string {
 *   switch (status) {
 *     case 'pending': return 'gray'
 *     case 'running': return 'blue'
 *     case 'completed': return 'green'
 *     case 'failed': return 'red'
 *     default: return assertNever(status) // Compiler error if case missed
 *   }
 * }
 * ```
 */

// ============================================================================
// Exhaustive Checking
// ============================================================================

/**
 * Ensures exhaustive handling in switch statements and conditionals.
 *
 * Place in the default case of a switch or the final else branch.
 * If all cases are handled, the code is unreachable (type is `never`).
 * If a case is missing, TypeScript will error because the type isn't `never`.
 *
 * @param x - Value that should be of type `never` if all cases handled
 * @throws Error if called at runtime (indicates a missing case)
 *
 * @example
 * ```typescript
 * type Shape = { type: 'circle'; radius: number } | { type: 'square'; side: number }
 *
 * function getArea(shape: Shape): number {
 *   switch (shape.type) {
 *     case 'circle':
 *       return Math.PI * shape.radius ** 2
 *     case 'square':
 *       return shape.side ** 2
 *     default:
 *       return assertNever(shape) // Error if new shape type added
 *   }
 * }
 * ```
 */
export function assertNever(x: never, message?: string): never {
  throw new Error(message ?? `Unexpected value: ${JSON.stringify(x)}`)
}

// ============================================================================
// Pattern Matching
// ============================================================================

/**
 * Type helper to extract the discriminator values from a union.
 */
type DiscriminatorValues<T, K extends keyof T> = T extends unknown ? T[K] : never

/**
 * Pattern matching for discriminated unions.
 *
 * Provides exhaustive, type-safe matching on tagged/discriminated unions.
 * The compiler ensures all variants are handled.
 *
 * @param value - The discriminated union value to match
 * @param discriminator - The property name used as the discriminator (e.g., 'type', 'kind')
 * @param handlers - Object mapping each discriminator value to a handler function
 * @returns The result of the matched handler
 *
 * @example
 * ```typescript
 * type Event =
 *   | { type: 'login'; userId: string }
 *   | { type: 'logout'; userId: string }
 *   | { type: 'purchase'; amount: number }
 *
 * const message = match(event, 'type', {
 *   login: (e) => `User ${e.userId} logged in`,
 *   logout: (e) => `User ${e.userId} logged out`,
 *   purchase: (e) => `Purchase of $${e.amount}`,
 * })
 * ```
 */
export function match<
  T extends Record<K, string>,
  K extends keyof T,
  R,
>(
  value: T,
  discriminator: K,
  handlers: {
    [V in DiscriminatorValues<T, K>]: (value: Extract<T, Record<K, V>>) => R
  }
): R {
  const key = value[discriminator] as DiscriminatorValues<T, K>
  const handler = handlers[key]
  return handler(value as Extract<T, Record<K, typeof key>>)
}

/**
 * Simpler pattern matching when discriminator is always 'type'.
 *
 * @example
 * ```typescript
 * type Result = { type: 'ok'; value: number } | { type: 'error'; message: string }
 *
 * const output = matchType(result, {
 *   ok: (r) => `Value: ${r.value}`,
 *   error: (r) => `Error: ${r.message}`,
 * })
 * ```
 */
export function matchType<
  T extends { type: string },
  R,
>(
  value: T,
  handlers: {
    [V in T['type']]: (value: Extract<T, { type: V }>) => R
  }
): R {
  return match(value, 'type', handlers)
}

// ============================================================================
// Exhaustive Records
// ============================================================================

/**
 * Creates a record that must have an entry for every value in a union type.
 *
 * The compiler will error if any union value is missing from the record.
 * Useful for lookup tables, label mappings, and configuration objects.
 *
 * @param record - Object with keys for every union value
 * @returns The same record, with type safety enforced
 *
 * @example
 * ```typescript
 * type Status = 'pending' | 'running' | 'completed' | 'failed'
 *
 * // Compiler error if any status is missing
 * const STATUS_LABELS = exhaustiveRecord<Status, string>({
 *   pending: 'Waiting to start',
 *   running: 'In progress',
 *   completed: 'Done',
 *   failed: 'Error occurred',
 * })
 * ```
 */
export function exhaustiveRecord<K extends string, V>(
  record: Record<K, V>
): Record<K, V> {
  return record
}

/**
 * Creates a type-safe lookup function from a record.
 *
 * @param record - Object mapping keys to values
 * @returns A function that safely looks up values by key
 *
 * @example
 * ```typescript
 * type Priority = 'low' | 'medium' | 'high'
 *
 * const getPriorityColor = createLookup<Priority, string>({
 *   low: 'green',
 *   medium: 'yellow',
 *   high: 'red',
 * })
 *
 * const color = getPriorityColor('high') // 'red'
 * ```
 */
export function createLookup<K extends string, V>(
  record: Record<K, V>
): (key: K) => V {
  return (key: K) => record[key]
}

// ============================================================================
// Type Guards for Discriminated Unions
// ============================================================================

/**
 * Creates a type guard for checking discriminated union variants.
 *
 * @param discriminator - The property name used as the discriminator
 * @param value - The discriminator value to check for
 * @returns A type guard function
 *
 * @example
 * ```typescript
 * type Event =
 *   | { type: 'click'; x: number; y: number }
 *   | { type: 'keypress'; key: string }
 *
 * const isClick = isOfType('type', 'click')
 *
 * if (isClick(event)) {
 *   console.log(event.x, event.y) // TypeScript knows event is click
 * }
 * ```
 */
export function isOfType<
  T extends Record<K, string>,
  K extends keyof T,
  V extends T[K],
>(
  discriminator: K,
  value: V
): (obj: T) => obj is Extract<T, Record<K, V>> {
  return (obj: T): obj is Extract<T, Record<K, V>> => obj[discriminator] === value
}

// ============================================================================
// Tagged Union Constructor
// ============================================================================

/**
 * Creates a factory function for constructing tagged union variants.
 *
 * @param type - The discriminator value for this variant
 * @returns A function that constructs the tagged variant
 *
 * @example
 * ```typescript
 * type Result<T> =
 *   | { type: 'ok'; value: T }
 *   | { type: 'error'; message: string }
 *
 * const ok = <T>(value: T) => taggedUnion('ok', { value })
 * const error = (message: string) => taggedUnion('error', { message })
 *
 * const result = ok(42) // { type: 'ok', value: 42 }
 * ```
 */
export function taggedUnion<
  Type extends string,
  Props extends Record<string, unknown>,
>(
  type: Type,
  props: Props
): { type: Type } & Props {
  return { type, ...props }
}

// ============================================================================
// Narrow Type Helper
// ============================================================================

/**
 * Narrows a union type to a specific variant.
 *
 * Useful for type assertions when you know more than TypeScript.
 *
 * @example
 * ```typescript
 * type Animal = { type: 'dog'; bark: () => void } | { type: 'cat'; meow: () => void }
 *
 * function handleDog(animal: Animal) {
 *   const dog = narrow<Animal, 'dog'>(animal)
 *   dog.bark() // TypeScript knows this is valid
 * }
 * ```
 */
export type Narrow<T, K extends T extends { type: string } ? T['type'] : never> =
  Extract<T, { type: K }>
